import { useMemo } from "react";
import { toast } from "react-toastify";

import { THEME_COLOR } from "constants/style.constants";
import _debounce from "lodash/debounce";

import { ColorPicker } from "components/base";
import { useProjectContext } from "components/project/projects/context";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { useShapefileDispatch } from "components/project/shapefiles/context";
import { useUpdateProjectShapefileFolderColourMutation } from "components/project/shapefiles/mutations";
import {
  INVALID_LINE_STYLE_MESSAGE,
  ShapefileStyleProp,
  type ShapefileStylePropT,
  isLineStyle
} from "components/project/shapefiles/shapefiles.constants";

export interface IProjectShapefileColorPickerProps {
  shapefileNodeId: string;
  lineStyle?: string;
  fillPattern?: string;
  projectId?: string;
  place?: "top" | "right" | "bottom" | "left";
}

export function ProjectShapefileFolderColorPicker(
  props: IProjectShapefileColorPickerProps
) {
  const { selectedProjectId } = useProjectContext();
  const projectId = props.projectId ?? selectedProjectId;
  const shapefileDisaptch = useShapefileDispatch();

  const projectPermissions = useSelectedProjectPermissions();
  const updateProjectShapefileFolderColour =
    useUpdateProjectShapefileFolderColourMutation({});

  const handleChange = (key: ShapefileStylePropT, value: string) => {
    if (key === ShapefileStyleProp.LineStyle && !isLineStyle(value)) {
      toast.error(INVALID_LINE_STYLE_MESSAGE);
      return;
    }
    updateProjectShapefileFolderColour.mutate({
      body: { key, value },
      projectId: projectId,
      shapefileNodeId: props.shapefileNodeId
    });
  };

  const debouncedUpdateProjectShapefileOpacity = useMemo(
    () =>
      _debounce(
        (value) =>
          updateProjectShapefileFolderColour.mutate({
            body: {
              key: "opacity",
              value
            },
            projectId: projectId,
            shapefileNodeId: props.shapefileNodeId
          }),
        500
      ),
    [projectId]
  );
  return (
    <div className="shapefile-color-picker-action">
      <ColorPicker
        isAtFolderLevel={true}
        onChange={(key, value) => {
          handleChange(key, value);
          shapefileDisaptch({ payload: { isLoadingColors: true } });
        }}
        onChangeOpacity={(key, value) =>
          debouncedUpdateProjectShapefileOpacity(value, props.shapefileNodeId)
        }
        onChangeLineStyle={(key, value) => handleChange(key, value)}
        onChangeFillPattern={(key, value) => handleChange(key, value)}
        values={{
          color: THEME_COLOR,
          strokeColor: THEME_COLOR,
          thickness: 2,
          lineStyle: props.lineStyle ?? "solid",
          fillPattern: props.fillPattern ?? "none",
          opacity: 100
        }}
        isReadOnly={!projectPermissions.canEditShapefiles}
        place={props.place}
      />
    </div>
  );
}
